import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal
 * Animates its children into view the first time they scroll into the
 * viewport, using CSS transitions driven by IntersectionObserver.
 * Falls back to an immediate, motion-free appearance for users who have
 * requested reduced motion.
 *
 * Props:
 *  - as: element/tag to render (default 'div')
 *  - animation: 'up' | 'left' | 'right' | 'scale'
 *  - delay: additional transition-delay in ms (useful for staggered lists)
 *  - threshold: fraction of the element that must be visible to trigger
 */
const Reveal = ({
  children,
  as: Tag = 'div',
  className = '',
  animation = 'up',
  delay = 0,
  threshold = 0.15,
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(node);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  const animationClass =
    animation === 'left'
      ? 'reveal-left'
      : animation === 'right'
      ? 'reveal-right'
      : animation === 'scale'
      ? 'reveal-scale'
      : 'reveal-up';

  return (
    <Tag
      ref={ref}
      className={`reveal ${animationClass} ${visible ? 'reveal-in' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
